import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField as MuiTextField,
} from "@mui/material";

import { styled } from "@mui/material/styles";

import { useEffect, useState } from "react";

import { useFetcher } from "react-router-dom";

import { pick, sort } from "radash";

import { useData } from "../../../hooks/useData";

const FILTER_KEYS = [
  "account_id",
  "pacing",
  "audience_type",
  "product_type",
  "floor_type",
];

const findAccount = (accounts, accountId) =>
  accounts.find(({ id }) => id.toString() === accountId);

const buildQueryFromURL = (url) => {
  const searchParams = Object.fromEntries(new URLSearchParams(url.search));

  return pick(searchParams, FILTER_KEYS);
};

const INPUT_VALIDATION_MESSAGES = {
  account_id: "Select Account",
  product_type: "Select Product Type",
  pacing: "Select Pacing Type",
  audience_type: "Select Audience Type",
  floor_type: "Select Floor Type to override",
};

const TextField = styled(({ ...props }) => (
  <MuiTextField {...props} sx={{ fontSize: 14 }} />
))({
  "& .MuiInputBase-root": {
    fontSize: 14,
  },
});

const AudienceFloorProductSetting = ({
  onChange,
  onClear,
  disableClear = false,
}) => {
  const [query, setQuery] = useState(buildQueryFromURL(window.location));
  const [errors, setErrors] = useState({});
  const { loading, data: accounts } = useData("managed_plus", "/accounts");
  const [account, setAccount] = useState(null);
  const [currentAction, setCurrentAction] = useState(null);

  const isPodcast = query.product_type === "podcast";
  const isBaseFloor = isPodcast && query.floor_type === "base";

  const handleChange = (newValue) =>
    setQuery((prev) => ({ ...prev, ...newValue }));
  const fetcher = useFetcher();

  useEffect(() => {
    let ignore = false;

    if (!ignore && accounts !== null) {
      const searchParams = new URLSearchParams(window.location.search);
      const account = findAccount(accounts, searchParams.get("account_id"));

      if (account) {
        setAccount(account);
      }
    }

    return () => {
      ignore = true;
    };
  }, [accounts]);

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      onChange({ query, data: fetcher.data, editing: true });

      fetcher.data = undefined;
    }
  }, [fetcher, query, onChange]);

  useEffect(() => {
    if (currentAction === "load" && fetcher.state === "idle") {
      setCurrentAction("loading");

      const { account_id, ...rest } = query;
      const params = Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value != null && value !== "")
      );
      const action = `/managed-plus/pricing/custom-audience-floors/${account_id}`;

      fetcher.submit(params, { action, method: "get" });
    }
  }, [fetcher, query, currentAction]);

  useEffect(() => {
    if (fetcher.state === "idle" && currentAction === "clear") {
      fetcher.data = undefined;
      setCurrentAction(null);
      setQuery(({ account_id }) => ({ account_id }));
      onClear({ editing: false });
    }
  }, [fetcher, currentAction, onClear]);

  return (
    <form
      method="get"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();

        if (currentAction === null) {
          if (e.target.checkValidity()) {
            setCurrentAction("load");
          } else {
            const errors = Array.from(
              document.querySelectorAll("[name]:invalid")
            ).reduce((acc, input) => {
              return {
                ...acc,
                [input.name]: INPUT_VALIDATION_MESSAGES[input.name],
              };
            }, {});

            setErrors(errors);
          }
        } else {
          setCurrentAction("clear");
        }
      }}
    >
      <Grid container rowSpacing={1} direction="column">
        <Grid item xs={12} sx={{ display: "flex" }}>
          <Autocomplete
            disablePortal
            options={sort(accounts ?? [], f => f.id)}
            isOptionEqualToValue={(left, right) => left.id === right.id}
            getOptionLabel={({ id }) => id.toString()}
            getOptionKey={({ id }) => `acc_${id}`}
            loading={loading}
            onChange={(e, account) => {
              setAccount(account);
              handleChange({ account_id: account?.id });
              setErrors(({ account_id, ...prev }) => prev);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account ID"
                size="small"
                helperText={errors["account_id"] ?? " "}
                FormHelperTextProps={{
                  error: true,
                  sx: { ml: 0 },
                }}
                required
                name="account_id"
              />
            )}
            fullWidth
            sx={{ maxWidth: 220, mr: 2 }}
            value={account ?? null}
          />
          <Autocomplete
            disablePortal
            options={accounts ?? []}
            getOptionKey={({ id }) => `company_acc_${id}`}
            getOptionLabel={({ name }) => name}
            onChange={(e, account) => {
              setAccount(account);
              handleChange({ account_id: account?.id });
              setErrors(({ account_id, ...prev }) => prev);
            }}
            loading={loading}
            isOptionEqualToValue={(left, right) => left.id === right.id}
            value={account ?? null}
            fullWidth
            sx={{ maxWidth: 220 }}
            renderInput={(params) => (
              <TextField {...params} label="Company Name" size="small" />
            )}
          />
        </Grid>
        <Grid item xs={12} sx={{ display: "flex" }}>
          <TextField
            size="small"
            label="Product"
            name="product_type"
            onChange={(e) => {
              const nextIsPodcast = e.target.value === "podcast";

              const newValue = {
                product_type: e.target.value,
                pacing: nextIsPodcast ? "uniques" : query.pacing,
                ...(nextIsPodcast ? {} : { floor_type: undefined }),
              };

              handleChange(newValue);
              setErrors(({ product_type, pacing, floor_type, ...prev }) => ({
                ...prev,
                ...(!nextIsPodcast && pacing && { pacing }),
              }));
            }}
            helperText={errors["product_type"] ?? " "}
            FormHelperTextProps={{
              error: true,
              sx: { ml: 0 },
            }}
            fullWidth
            sx={{ maxWidth: 220, mr: 2 }}
            select
            required
            value={query.product_type ?? ""}
          >
            <MenuItem key="page_view" value="page_view">
              PageView
            </MenuItem>
            <MenuItem key="podcast" value="podcast">
              Podcast
            </MenuItem>
          </TextField>
          {isPodcast ? (
            <TextField
              size="small"
              label="Floor Type"
              name="floor_type"
              helperText={errors["floor_type"] ?? " "}
              FormHelperTextProps={{
                error: true,
                sx: { ml: 0 },
              }}
              onChange={(e) => {
                const nextIsBase = e.target.value === "base";

                handleChange({
                  floor_type: e.target.value,
                  ...(nextIsBase && { audience_type: undefined }),
                });
                setErrors(({ floor_type, audience_type, ...prev }) => prev);
              }}
              fullWidth
              select
              required
              value={query.floor_type ?? ""}
              sx={{ maxWidth: 220, mr: 2 }}
            >
              <MenuItem key="audience" value="audience">
                Audience Floors
              </MenuItem>
              <MenuItem key="base" value="base">
                Base Floors
              </MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              label="Pacing"
              name="pacing"
              helperText={errors["pacing"] ?? " "}
              FormHelperTextProps={{
                error: true,
                sx: { ml: 0 },
              }}
              onChange={(e) => {
                handleChange({ pacing: e.target.value });
                setErrors(({ pacing, ...prev }) => prev);
              }}
              fullWidth
              select
              required
              value={query.pacing ?? ""}
              sx={{ maxWidth: 220, mr: 2 }}
            >
              <MenuItem key="fulfillment" value="fulfillment">
                Fulfillment
              </MenuItem>
              <MenuItem key="uniques" value="uniques">
                Uniques
              </MenuItem>
            </TextField>
          )}
          <TextField
            size="small"
            label="Audience"
            name="audience_type"
            fullWidth
            onChange={(e) => {
              handleChange({ audience_type: e.target.value });
              setErrors(({ audience_type, ...prev }) => prev);
            }}
            helperText={errors["audience_type"] ?? " "}
            FormHelperTextProps={{
              error: true,
              sx: { ml: 0 },
            }}
            select={!isBaseFloor}
            required={!isBaseFloor}
            disabled={isBaseFloor}
            value={isBaseFloor ? "N/A" : query.audience_type ?? ""}
            sx={{ maxWidth: 220, mr: 2 }}
          >
            <MenuItem key="pharma" value="pharma">
              Pharma
            </MenuItem>
            <MenuItem key="non_pharma" value="non_pharma">
              Non-Pharma
            </MenuItem>
          </TextField>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            <Button
              variant="contained"
              type="submit"
              disabled={currentAction !== null && disableClear}
            >
              {currentAction === null ? "Apply" : "Clear"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default AudienceFloorProductSetting;
